// Test-search panel — semantic retrieval validation for editorial.
//
// Route: /admin/insights/rag (the existing nav slot reserved for RAG
// pipeline testing). Sits in INSIGHTS section so it's grouped with
// Benchmark + Labs.
//
// V1.x scope: query-only side of the RAG pipeline. Tests that authored
// entries actually surface for the right queries. The full
// "compose recommendation + plan from retrieval" path lands in Phase 4
// (POST /coaching/respond). When that lands, this page gains a second
// tab "Compose response" alongside "Search test".
//
// RBAC: gated by `kb:write` (super_admin + content_editor only) per the
// navModel — same scope as the rest of the editorial surfaces.

import { useState, type FormEvent } from 'react';
import { Button, Spinner, TextInput } from '@gc-coach/design-system';
import type {
  Domain,
  KBEntryLanguage,
  KBEntryType,
  SearchKBEntryResult,
} from '@gc-coach/shared';
import { ALL_DOMAINS, ALL_KB_ENTRY_TYPES } from '@gc-coach/shared';
import { AdminShell, PageHeader } from '../shell/index.js';
import { useI18n } from '../i18n/index.js';
import { searchKBEntries } from '../api/endpoints.js';
import styles from './KBSearchPage.module.css';

const LANGUAGES: ReadonlyArray<KBEntryLanguage> = ['en', 'fr'];

// Cap the per-page top_k at 50 — matches the Atlas Vector Search soft cap
// the backend enforces. Lets editors stress-test broader queries without
// having to know that limit.
const TOP_K_OPTIONS = [5, 10, 25, 50];

interface SearchState {
  kind: 'idle' | 'loading' | 'success' | 'error' | 'empty';
  results: ReadonlyArray<SearchKBEntryResult>;
  errorCode: string | null;
  query: string;
}

const INITIAL_STATE: SearchState = {
  kind: 'idle',
  results: [],
  errorCode: null,
  query: '',
};

export function KBSearchPage() {
  const { t, tt } = useI18n();

  // Form state
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<KBEntryType | ''>('');
  const [domainFilter, setDomainFilter] = useState<Domain | ''>('');
  const [langFilter, setLangFilter] = useState<KBEntryLanguage | ''>('');
  const [includeUnpublished, setIncludeUnpublished] = useState(false);
  const [topK, setTopK] = useState(10);

  // Result state — separate from form so the panel keeps the prior results
  // visible while the next query is being typed.
  const [search, setSearch] = useState<SearchState>(INITIAL_STATE);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = query.trim();
    if (trimmed.length === 0) return;

    setSearch({ kind: 'loading', results: [], errorCode: null, query: trimmed });

    const result = await searchKBEntries({
      query: trimmed,
      top_k: topK,
      ...(typeFilter ? { type: typeFilter } : {}),
      ...(domainFilter ? { domain: domainFilter } : {}),
      ...(langFilter ? { language: langFilter } : {}),
      ...(includeUnpublished ? { include_unpublished: true } : {}),
    });

    if (!result.ok) {
      setSearch({
        kind: 'error',
        results: [],
        errorCode: result.code,
        query: trimmed,
      });
      return;
    }

    setSearch({
      kind: result.data.results.length === 0 ? 'empty' : 'success',
      results: result.data.results,
      errorCode: null,
      query: trimmed,
    });
  }

  function openEntry(id: string) {
    // Open the entry's editor in a new tab so the user keeps the search
    // results panel open. Editorial use case: "did my framework surface
    // for this query? let me click in and verify the body."
    window.open(`/admin/knowledge/base/${id}`, '_blank', 'noopener,noreferrer');
  }

  return (
    <AdminShell
      activeKey="rag"
      breadcrumb={[t('kbSearch.breadcrumbInsights'), t('kbSearch.breadcrumbRag')]}
      state={{ kind: 'normal' }}
    >
      <PageHeader
        title={t('kbSearch.title')}
        sub={t('kbSearch.subTitle')}
      />

      <form className={styles.searchForm} onSubmit={onSubmit}>
        <label className={styles.queryLabel} htmlFor="kb-search-query">
          {t('kbSearch.queryLabel')}
        </label>
        <textarea
          id="kb-search-query"
          className={styles.queryInput}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('kbSearch.queryPlaceholder')}
          rows={3}
          disabled={search.kind === 'loading'}
        />

        <div className={styles.filterRow}>
          <div className={styles.filterField}>
            <label className={styles.filterLabel} htmlFor="kb-search-type">
              {t('kbSearch.typeLabel')}
            </label>
            <select
              id="kb-search-type"
              className={styles.filterSelect}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value as KBEntryType | '')}
            >
              <option value="">{t('kbSearch.typeAll')}</option>
              {ALL_KB_ENTRY_TYPES.map((tp) => (
                <option key={tp} value={tp}>
                  {t(`kb.type${typeLabelKey(tp)}`)}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.filterField}>
            <label className={styles.filterLabel} htmlFor="kb-search-domain">
              {t('kbSearch.domainLabel')}
            </label>
            <select
              id="kb-search-domain"
              className={styles.filterSelect}
              value={domainFilter}
              onChange={(e) => setDomainFilter(e.target.value as Domain | '')}
            >
              <option value="">{t('kbSearch.domainAll')}</option>
              {ALL_DOMAINS.map((d) => (
                <option key={d} value={d}>
                  {t(`kb.domain.${d}`)}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.filterField}>
            <label className={styles.filterLabel} htmlFor="kb-search-lang">
              {t('kbSearch.langLabel')}
            </label>
            <select
              id="kb-search-lang"
              className={styles.filterSelect}
              value={langFilter}
              onChange={(e) => setLangFilter(e.target.value as KBEntryLanguage | '')}
            >
              <option value="">{t('kbSearch.langAll')}</option>
              {LANGUAGES.map((l) => (
                <option key={l} value={l}>
                  {l.toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.filterField}>
            <label className={styles.filterLabel} htmlFor="kb-search-topk">
              {t('kbSearch.topKLabel')}
            </label>
            <select
              id="kb-search-topk"
              className={styles.filterSelect}
              value={topK}
              onChange={(e) => setTopK(Number(e.target.value))}
            >
              {TOP_K_OPTIONS.map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </div>

          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={includeUnpublished}
              onChange={(e) => setIncludeUnpublished(e.target.checked)}
            />
            <span>{t('kbSearch.includeUnpublishedLabel')}</span>
          </label>
        </div>

        <div className={styles.submitRow}>
          <Button kind="primary" type="submit" disabled={search.kind === 'loading' || !query.trim()}>
            {search.kind === 'loading' ? <Spinner /> : t('kbSearch.submitButton')}
          </Button>
          <span className={styles.hint}>{t('kbSearch.hint')}</span>
        </div>
      </form>

      <div className={styles.resultsArea}>
        {search.kind === 'idle' && (
          <div className={styles.resultsEmpty}>
            <div className={styles.resultsEmptyIcon} aria-hidden="true">⌕</div>
            <strong>{t('kbSearch.idleTitle')}</strong>
            <p>{t('kbSearch.idleBody')}</p>
          </div>
        )}

        {search.kind === 'error' && (
          <div className={styles.resultsError}>
            <strong>{t('kbSearch.errorTitle')}</strong>
            <p>
              {search.errorCode === 'UPSTREAM_ERROR'
                ? t('kbSearch.errorUpstream')
                : t('kbSearch.errorGeneric')}
            </p>
          </div>
        )}

        {search.kind === 'empty' && (
          <div className={styles.resultsEmpty}>
            <strong>{t('kbSearch.noResultsTitle')}</strong>
            <p>{tt('kbSearch.noResultsBody', { query: search.query })}</p>
          </div>
        )}

        {search.kind === 'success' && (
          <>
            <div className={styles.resultsMeta}>
              {tt('kbSearch.resultsCount', { n: search.results.length })}
              <span className={styles.resultsQuery}>« {search.query} »</span>
            </div>
            <ol className={styles.resultsList}>
              {search.results.map((r, i) => (
                <li key={r.entry.id} className={styles.resultRow}>
                  <button
                    type="button"
                    className={styles.resultRowButton}
                    onClick={() => openEntry(r.entry.id)}
                    title={t('kbSearch.openEntryTooltip')}
                  >
                    <div className={styles.resultRank}>{i + 1}</div>
                    <div className={styles.resultMain}>
                      <div className={styles.resultPills}>
                        <span className={`${styles.typePill} ${typePillClass(r.entry.type, styles)}`}>
                          {t(`kb.type${typeLabelKey(r.entry.type)}`)}
                        </span>
                        <span className={styles.domainBadge}>
                          {t(`kb.domain.${r.entry.domain}`)}
                        </span>
                        <span className={styles.langChip}>{r.entry.language.toUpperCase()}</span>
                        <span className={statusPillClass(r.entry.status, styles)}>
                          {statusText(r.entry.status, t)}
                        </span>
                      </div>
                      <div className={styles.resultTitle}>{r.entry.title || t('kb.untitled')}</div>
                      <div className={styles.resultMetaLine}>
                        <span>{r.entry.contributor}</span>
                        <span>·</span>
                        <span>v{r.entry.version}</span>
                      </div>
                    </div>
                    <div className={styles.resultScore}>
                      <div className={styles.scoreBarTrack}>
                        <div
                          className={styles.scoreBarFill}
                          style={{ width: `${Math.max(0, Math.min(1, r.score)) * 100}%` }}
                        />
                      </div>
                      <div className={styles.scoreValue}>{(r.score * 100).toFixed(1)}%</div>
                    </div>
                  </button>
                </li>
              ))}
            </ol>
          </>
        )}
      </div>
    </AdminShell>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function typeLabelKey(t: KBEntryType): 'QaPair' | 'Framework' | 'CaseStudy' | 'Excerpt' {
  switch (t) {
    case 'qa_pair':
      return 'QaPair';
    case 'framework':
      return 'Framework';
    case 'case_study':
      return 'CaseStudy';
    case 'document_excerpt':
      return 'Excerpt';
  }
}

function typePillClass(t: KBEntryType, cls: Record<string, string>): string {
  if (t === 'qa_pair') return cls['typePillQaPair'] ?? '';
  if (t === 'framework') return cls['typePillFramework'] ?? '';
  if (t === 'case_study') return cls['typePillCaseStudy'] ?? '';
  return cls['typePillExcerpt'] ?? '';
}

function statusPillClass(
  s: string,
  cls: Record<string, string>,
): string {
  const base = cls['statusPill'] ?? '';
  if (s === 'draft') return `${base} ${cls['statusDraft'] ?? ''}`;
  if (s === 'in_review') return `${base} ${cls['statusInReview'] ?? ''}`;
  if (s === 'published') return `${base} ${cls['statusPublished'] ?? ''}`;
  if (s === 'archived') return `${base} ${cls['statusArchived'] ?? ''}`;
  return `${base} ${cls['statusRemoved'] ?? ''}`;
}

function statusText(s: string, t: (k: string) => string): string {
  if (s === 'draft') return t('kb.statusDraft');
  if (s === 'in_review') return t('kb.statusInReview');
  if (s === 'published') return t('kb.statusPublished');
  if (s === 'archived') return t('kb.statusArchived');
  return t('kb.statusRemoved');
}

// Silence "import not used" for TextInput — referenced via shell types
// indirectly; explicit to avoid eslint hassle.
void TextInput;
